'use client';

import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const monthlyData = [
  { month: 'Jan', bookings: 420, revenue: 38 }, { month: 'Feb', bookings: 380, revenue: 34 },
  { month: 'Mar', bookings: 510, revenue: 42 }, { month: 'Apr', bookings: 620, revenue: 58 },
  { month: 'May', bookings: 580, revenue: 51 }, { month: 'Jun', bookings: 740, revenue: 74 },
  { month: 'Jul', bookings: 890, revenue: 89 }, { month: 'Aug', bookings: 1089, revenue: 96 },
];

const propertyTypeData = [
  { name: 'Villas', value: 28 },
  { name: 'Apartments', value: 35 },
  { name: 'Lodges', value: 18 },
  { name: 'Cottages', value: 12 },
  { name: 'Other', value: 7 },
];

const COLORS = ['#f43f5e', '#fb923c', '#fbbf24', '#34d399', '#60a5fa'];

const topProperties = [
  { name: 'Luxury Villa Kololo', bookings: 48, revenue: 'UGX 120M', rating: 4.9 },
  { name: 'Safari Lodge Murchison', bookings: 36, revenue: 'UGX 115M', rating: 4.8 },
  { name: 'Lakeside Retreat Entebbe', bookings: 52, revenue: 'UGX 62M', rating: 4.7 },
  { name: 'Mountain Cabin Rwenzori', bookings: 29, revenue: 'UGX 26M', rating: 4.6 },
  { name: 'City Apartment Kampala', bookings: 71, revenue: 'UGX 32M', rating: 4.3 },
];

export default function AdminReports() {
  const [period, setPeriod] = useState<'monthly' | 'quarterly'>('monthly');

  return (
    <div>
      {/* Period Toggle */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-bold text-gray-900">Platform Analytics</h2>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {(['monthly', 'quarterly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                period === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Bookings & Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar yAxisId="left" dataKey="bookings" fill="#fda4af" radius={[3, 3, 0, 0]} name="Bookings" />
              <Bar yAxisId="right" dataKey="revenue" fill="#f43f5e" radius={[3, 3, 0, 0]} name="Revenue (M)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Property Types</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={propertyTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                {propertyTypeData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
              <Tooltip formatter={(v) => [`${v}%`, '']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Properties */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Top Performing Properties</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Property</th>
                <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bookings</th>
                <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenue</th>
                <th className="text-left pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rating</th>
              </tr>
            </thead>
            <tbody>
              {topProperties.map((p, i) => (
                <tr key={p.name} className="border-b border-gray-50 last:border-0">
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-[#4088C6]/10 text-[#4088C6] rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span className="text-sm font-semibold text-gray-900">{p.name}</span>
                    </div>
                  </td>
                  <td className="py-3 text-sm text-gray-700">{p.bookings}</td>
                  <td className="py-3 text-sm font-bold text-gray-900">{p.revenue}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-1">
                      <span className="text-amber-400 text-sm">★</span>
                      <span className="text-sm font-semibold text-gray-700">{p.rating}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export */}
      <div className="flex gap-3">
        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#4088C6] text-white text-sm font-semibold rounded-xl hover:bg-[#3070a6] transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export PDF
        </button>
        <button className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>
    </div>
  );
}
